"use client";

import Button from "@/components/Button";
import LazyIcon from "@/components/icons/LazyIcon";
import LogoXfinity from "@/components/logos/LogoXfinity";
import PhoneNumberButton from "@/components/PhoneNumberButton";
import { useMonarchMetadata } from "@/context/MonarchMetadataContext";
import useElementViewed from "@/hooks/tagular/use-element-viewed";
import cn from "@/lib/cn";
import fireElementClicked from "@/lib/tagular/fire-element-clicked";
import { resolveXfinityClickOffUrl } from "@/lib/clickthrough-destination";
import { useEffect, useRef, useState } from "react";
import { ContentContainer } from "../ContentContainer";
import NavBarLocalization from "./NavBarLocalization";
import type { NavBarProps } from "./types";

const LOCALIZATION_REVEAL_SCROLL_RATIO = 0.25;
const LOCALIZATION_REVEAL_TIMING = {
  duration: 600,
  easing: "cubic-bezier(0.4, 0, 0.2, 1)",
  fill: "backwards",
} as const;

// when isIconOnlyOnMobile, ≤356px forces icon-only Call.

const NavBar: React.FC<NavBarProps> = ({
  ctaSequence,
  orderCta,
  phoneCta,
  signIn,
  localization,
}) => {
  const navRef = useRef<HTMLElement>(null);
  const localizationRowRef = useRef<HTMLDivElement>(null);
  const [isLocalizationRevealed, setIsLocalizationRevealed] = useState(false);
  const monarchMetadata = useMonarchMetadata();
  const hasLocalization = Boolean(localization);
  const isIconOnlyOnMobile = Boolean(phoneCta?.isIconOnlyOnMobile);
  const isPhoneCtaFirst = ctaSequence === "phoneFirst";
  const normalizedOrderCtaUrl = resolveXfinityClickOffUrl(orderCta?.url);

  useEffect(() => {
    if (!hasLocalization || isLocalizationRevealed) return;

    const handleScroll = () => {
      const scrollableHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      if (scrollableHeight <= 0) return;

      if (
        window.scrollY / scrollableHeight >=
        LOCALIZATION_REVEAL_SCROLL_RATIO
      ) {
        setIsLocalizationRevealed(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hasLocalization, isLocalizationRevealed]);

  useEffect(() => {
    if (!isLocalizationRevealed) return;

    const row = localizationRowRef.current;

    if (!row || typeof row.animate !== "function") return;

    // The row is always in place from md up, so there is nothing to reveal.
    if (!window.matchMedia("(max-width: 767px)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const { height: rowHeight } = row.getBoundingClientRect();

    const animation = row.animate(
      [{ marginTop: `-${rowHeight}px` }, { marginTop: "0px" }],
      LOCALIZATION_REVEAL_TIMING
    );

    return () => {
      animation.cancel();
    };
  }, [isLocalizationRevealed]);

  useElementViewed(navRef, {
    elementType: "navigation",
    location: "header",
    name: "navBar",
  });

  const handleOrderCtaClick = () => {
    const mergedTagular = {
      ...orderCta?.tagularProps,
      elementType:
        orderCta?.tagularProps?.elementType ?? ("ctaButton" as const),
      name: orderCta?.tagularProps?.name ?? orderCta?.elementName,
      text: orderCta?.tagularProps?.text ?? orderCta?.copy,
      outboundUrl:
        resolveXfinityClickOffUrl(orderCta?.tagularProps?.outboundUrl) ??
        normalizedOrderCtaUrl,
      ...(monarchMetadata && { monarchMetadata }),
    };
    fireElementClicked(mergedTagular);
  };

  const handleSignInClick = () => {
    const mergedTagular = {
      ...signIn?.tagularProps,
      elementType: signIn?.tagularProps?.elementType ?? ("textLink" as const),
      name: signIn?.tagularProps?.name ?? signIn?.elementName,
      text: signIn?.tagularProps?.text ?? signIn?.copy,
      outboundUrl: signIn?.tagularProps?.outboundUrl ?? signIn?.url,
      ...(monarchMetadata && { monarchMetadata }),
    };
    fireElementClicked(mergedTagular);
  };

  const orderIconVisibility = cn(
    orderCta?.showIcon?.mobile === false ? "hidden" : "flex",
    orderCta?.showIcon?.tablet === false ? "md:hidden" : "md:flex",
    orderCta?.showIcon?.desktop === false ? "lg:hidden" : "lg:flex"
  );

  // Mobile show* first; isIconOnlyOnMobile overrides icon≤356 / label+number from 357 up.
  const phoneIconVisibility = cn(
    phoneCta?.showIcon?.mobile === false
      ? isIconOnlyOnMobile
        ? "max-[356px]:flex min-[357px]:hidden"
        : "hidden"
      : "flex",
    phoneCta?.showIcon?.tablet === false ? "md:hidden" : "md:flex",
    phoneCta?.showIcon?.desktop === false ? "lg:hidden" : "lg:flex"
  );

  const orderCtaVisibility = cn(
    orderCta?.show?.mobile === false ? "hidden" : "flex",
    orderCta?.show?.tablet === false ? "md:hidden" : "md:flex",
    orderCta?.show?.desktop === false ? "lg:hidden" : "lg:flex"
  );

  const phoneCtaVisibility = cn(
    phoneCta?.show?.mobile === false ? "hidden" : "flex",
    phoneCta?.show?.tablet === false ? "md:hidden" : "md:flex",
    phoneCta?.show?.desktop === false ? "lg:hidden" : "lg:flex"
  );

  const phoneLabelVisibility = cn(
    phoneCta?.showLabel?.mobile === false
      ? "hidden"
      : isIconOnlyOnMobile
        ? "hidden min-[357px]:inline"
        : "inline",
    phoneCta?.showLabel?.tablet === false ? "md:hidden" : "md:inline",
    phoneCta?.showLabel?.desktop === false ? "lg:hidden" : "lg:inline"
  );

  const phoneNumberVisibility = cn(
    phoneCta?.showPhoneNumber?.mobile === false
      ? "hidden"
      : isIconOnlyOnMobile
        ? "hidden min-[357px]:inline"
        : "inline",
    phoneCta?.showPhoneNumber?.tablet === false ? "md:hidden" : "md:inline",
    phoneCta?.showPhoneNumber?.desktop === false ? "lg:hidden" : "lg:inline"
  );

  const phoneBorderVisibility = cn(
    phoneCta?.showBorder?.mobile === false ? "border-0" : "border-2",
    phoneCta?.showBorder?.tablet === false ? "md:border-0" : "md:border-2",
    phoneCta?.showBorder?.desktop === false ? "lg:border-0" : "lg:border-2"
  );

  const localizationVisibility = cn(
    localization?.show?.mobile === false || !isLocalizationRevealed
      ? "hidden"
      : "flex",
    localization?.show?.tablet === false ? "md:hidden" : "md:flex",
    localization?.show?.desktop === false ? "lg:hidden" : "lg:flex"
  );

  return (
    <nav
      ref={navRef}
      className="w-full bg-white shadow-sm"
      aria-label="Main navigation"
    >
      <ContentContainer
        className={cn(
          "pb-4 pt-6",
          localization
            ? "grid grid-cols-[1fr_auto] items-center gap-x-6 md:flex md:justify-between md:gap-6"
            : "flex items-center justify-between"
        )}
      >
        {/* This needs the be an <a> to prevent SPA navigation to our page root instead of xfinity's page */}
        {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
        <a href="/?INTCMP=ILC:MA:GEN:GEN5b9ab8ad1e540">
          <LogoXfinity
            theme="black"
            className="h-7 w-auto shrink-0"
            title="Xfinity"
          />
        </a>
        <div
          className={cn(
            "contents md:flex items-center gap-6",
            localization && "md:min-w-0 md:flex-1 md:justify-end"
          )}
        >
          {/* The mobile grid gives every child its own cell, so the CTAs need to
              be grouped into one cell to stay on the logo's row */}
          <div
            className={cn(
              localization ? "flex items-center gap-6" : "contents"
            )}
          >
            {/* Order CTA */}
            {orderCta?.copy && (
              <div
                className={cn(
                  "items-center",
                  orderCtaVisibility,
                  isPhoneCtaFirst && "max-md:order-2"
                )}
              >
                <Button
                  href={normalizedOrderCtaUrl}
                  display="fill"
                  tone="theme1"
                  size="sm"
                  className="rounded-full px-4 py-2 gap-2 title-tiny-semibold whitespace-nowrap"
                  aria-label={orderCta.ariaLabel}
                  onClick={handleOrderCtaClick}
                >
                  {orderCta.icon && (
                    <span className={orderIconVisibility}>
                      <LazyIcon
                        name={orderCta.icon}
                        className="size-5"
                        theme="white"
                        style="outline"
                      />
                    </span>
                  )}
                  {orderCta.copy}
                </Button>
              </div>
            )}

            {/* Phone CTA */}
            {phoneCta?.copy && (
              <div
                className={cn(
                  "items-center",
                  phoneCtaVisibility,
                  isPhoneCtaFirst && "max-md:order-1"
                )}
              >
                <PhoneNumberButton
                  label={phoneCta.copy}
                  labelMobile={phoneCta.copyMobile}
                  labelTablet={phoneCta.copyTablet}
                  labelClassName={phoneLabelVisibility}
                  phoneNumberClassName={phoneNumberVisibility}
                  className={phoneBorderVisibility}
                  icon={
                    phoneCta.icon ? (
                      <LazyIcon
                        name={phoneCta.icon}
                        className={cn("size-5", phoneIconVisibility)}
                        theme="purple"
                        style="outline"
                      />
                    ) : undefined
                  }
                  iconClassName={phoneIconVisibility}
                  tagularProps={{
                    ...phoneCta.tagularProps,
                    name: phoneCta.tagularProps?.name ?? phoneCta.elementName,
                    ...(monarchMetadata && { monarchMetadata }),
                  }}
                />
              </div>
            )}

            {/* Sign In */}
            {signIn?.copy && signIn?.url && (
              <a
                href={signIn.url || undefined}
                className={cn(
                  "title-xs-normal text-neutral-base whitespace-nowrap cursor-pointer no-underline hover:underline",
                  isPhoneCtaFirst && "max-md:order-3"
                )}
                aria-label={signIn.ariaLabel}
                onClick={handleSignInClick}
              >
                {signIn.copy}
              </a>
            )}
          </div>

          {localization && (
            <div
              className={cn(
                "col-span-2 min-w-0 md:col-auto md:w-full md:min-w-0 md:flex-1 md:max-w-140.25",
                "max-md:-mx-6 max-md:overflow-hidden",
                localizationVisibility
              )}
            >
              <div
                ref={localizationRowRef}
                className="w-full max-md:bg-position-[0_16px] max-md:px-6 max-md:pt-8 max-md:shadow-edge-top"
              >
                <NavBarLocalization
                  elementName={localization.elementName}
                  label={localization.label}
                  input={localization.input}
                  ctas={localization.ctas}
                  tagularProps={localization.tagularProps}
                />
              </div>
            </div>
          )}
        </div>
      </ContentContainer>
    </nav>
  );
};

export default NavBar;
